/**
 * Language Simplifier Utility
 *
 * Converts technical design/content terminology into plain language
 * for users who prefer simplified feedback.
 */

// Technical term mappings to simple language
const termMappings = {
  // Design terms
  'visual hierarchy': 'how easy it is to see what\'s important',
  'whitespace': 'empty space',
  'typography': 'text style',
  'typography hierarchy': 'text size levels',
  'color palette': 'color choices',
  'alignment': 'how things line up',
  'contrast': 'difference between colors',
  'consistency': 'keeping things the same',
  'layout': 'how things are arranged',
  'grid system': 'organized spacing',
  'focal point': 'where your eye goes first',
  'visual weight': 'how much attention something grabs',
  'balance': 'even distribution',
  'proximity': 'spacing',
  'scale': 'size',
  'proportion': 'size relationships',
  'margins': 'space around the edges',
  'gutters': 'spacing between sections',
  'slide edge': 'edge of the slide',
  'design quality': 'how good it looks',
  'design principles': 'design rules',
  'H1, H2, Body': 'heading levels (big, medium, small text)',
  'Captions/Meta': 'small text labels',
  'Pull Quotes': 'highlighted quotes',
  'color difference': 'how different the colors are',
  'adjacent levels': 'text right next to each other',
  'blur hierarchy': 'make it hard to see what\'s important',

  // Content terms
  'narrative flow': 'story flow',
  'text density': 'amount of text',
  'readability': 'how easy to read',
  'comprehension': 'understanding',
  'conciseness': 'being brief',
  'clarity': 'how clear the message is',
  'coherence': 'how ideas connect',
  'structure': 'organization',
  'transition': 'connection between ideas',
  'headline': 'title',
  'call to action': 'what you want people to do',
  'key message': 'main point',
  'supporting points': 'details',
};

/**
 * Simplifies a technical recommendation into plain language
 * @param {string} text - The technical recommendation text
 * @param {string} expertiseLevel - 'beginner' or 'expert'
 * @returns {string} - Simplified or original text based on expertise level
 */
export function simplifyRecommendation(text, expertiseLevel = 'beginner') {
  if (expertiseLevel === 'expert') {
    return text; // Return original technical language
  }

  let simplifiedText = text;

  // Replace technical terms with simple equivalents
  Object.entries(termMappings).forEach(([technical, simple]) => {
    const regex = new RegExp(technical, 'gi');
    simplifiedText = simplifiedText.replace(regex, simple);
  });

  // Simplify common phrases
  const phraseMappings = [
    // Complex sentence structures
    { from: /define and respect margins/gi, to: 'add space around the edges' },
    { from: /nudge content inward so nothing touches the slide edge/gi, to: 'move things away from the edges' },
    { from: /keep margins consistent across the deck/gi, to: 'use the same edge spacing on all slides' },
    { from: /use at least 3 levels \(H1, H2, Body\)/gi, to: 'use at least 3 different text sizes (big title, subtitle, body text)' },
    { from: /define H1, H2, Body at minimum/gi, to: 'use big titles, subtitles, and normal text' },
    { from: /then add Captions\/Meta or Pull Quotes as needed/gi, to: 'add small labels or quotes if needed' },
    { from: /clear size difference between colors between levels/gi, to: 'make text sizes clearly different' },
    { from: /clear size contrast between levels/gi, to: 'make text sizes clearly different' },
    { from: /use a ratio ≥ 1\.15 between adjacent levels/gi, to: 'make each text size at least 15% bigger than the next' },
    { from: /avoid 1-2pt\/px differences that blur hierarchy/gi, to: 'don\'t use text sizes that are too similar' },

    // Single word simplifications
    { from: /utilize/gi, to: 'use' },
    { from: /leverage/gi, to: 'use' },
    { from: /implement/gi, to: 'add' },
    { from: /optimize/gi, to: 'improve' },
    { from: /enhance/gi, to: 'make better' },
    { from: /incorporate/gi, to: 'include' },
    { from: /ensure/gi, to: 'make sure' },
    { from: /establish/gi, to: 'create' },
    { from: /facilitate/gi, to: 'help' },
    { from: /prioritize/gi, to: 'focus on' },
    { from: /consider/gi, to: 'think about' },
    { from: /avoid utilizing/gi, to: 'don\'t use' },
  ];

  phraseMappings.forEach(({ from, to }) => {
    simplifiedText = simplifiedText.replace(from, to);
  });

  return simplifiedText;
}

/**
 * Gets appropriate label for expertise level
 * @param {string} expertiseLevel - 'beginner' or 'expert'
 * @returns {object} - Label configuration
 */
export function getExpertiseLevelConfig(expertiseLevel) {
  if (expertiseLevel === 'expert') {
    return {
      label: 'Technical Analysis',
      description: 'Detailed design and content terminology',
      icon: '🔬',
      color: 'text-purple-600'
    };
  }

  return {
    label: 'Simplified Feedback',
    description: 'Plain language recommendations',
    icon: '💬',
    color: 'text-blue-600'
  };
}

/**
 * Simplifies metric labels
 * @param {string} label - Original metric label
 * @param {string} expertiseLevel - 'beginner' or 'expert'
 * @returns {string} - Simplified or original label
 */
export function simplifyMetricLabel(label, expertiseLevel = 'beginner') {
  if (expertiseLevel === 'expert') {
    return label;
  }

  const metricMappings = {
    'Overall Content Score': 'How Good is the Content',
    'Overall Design Score': 'How Good is the Design',
    'Overall Score': 'Overall Quality',
    'Est. Time to Present': 'How Long to Present',
    'Brand Compliance': 'Brand Guidelines Match',
    'Slides Need Edits': 'Slides to Improve',
    'Narrative Score': 'Story Flow Score',
    'Grammatical Errors': 'Spelling & Grammar Issues',
    'Data Visualization': 'Chart Quality',
    'Engagement Potential': 'How Engaging It Is',
    'Top Issue Category': 'Most Common Problem',
  };

  return metricMappings[label] || label;
}

/**
 * Simplifies section headers
 * @param {string} header - Original section header
 * @param {string} expertiseLevel - 'beginner' or 'expert'
 * @returns {string} - Simplified or original header
 */
export function simplifySectionHeader(header, expertiseLevel = 'beginner') {
  if (expertiseLevel === 'expert') {
    return header;
  }

  const headerMappings = {
    'Content Refinement': 'Content Improvements',
    'Visual Content Suggestions': 'Visual Improvements',
    'Visual Enhancement Suggestions': 'Ways to Improve Visuals',
    'Visual Design Quality': 'Design Quality',
    'Design Quality Panel': 'Design Check',
    'Suggested Title': 'Better Title',
    'Multi-Dimensional Heatmap': 'Slide Quality Map',
    'Top Issue Categories': 'Most Common Problems',
    'Unified Priorities': 'What to Fix First',
  };

  return headerMappings[header] || header;
}
